import { odata } from '@bingads-webui/http-util';
import { isProdEnvironment } from '@bingads-webui-campaign/campaign-ui-common';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';
import { useCallback } from 'react';

import { extractCacheRelatedId, extractMkt, fallbackMkt } from './utils';

const getRequestUrl = () => {
  const isProd = isProdEnvironment(window.location.hostname);
  return isProd ? 'https://adrecommendation.api.ads.microsoft.com' : 'https://adrecommendation.api.sandbox.ads.microsoft.com';
};

const DefaultOptions = {
  contentType: 'application/json',
  host: getRequestUrl(),
};

const getResumeUrl = (signUpUrl, email) => {
  const resumeUrl = new URL(signUpUrl);
  if (!isProdEnvironment(window.location.hostname)) {
    resumeUrl.protocol = 'https:';
    resumeUrl.host = 'ads-int.microsoft.com';
  }
  resumeUrl.searchParams.set('email', email);
  return resumeUrl.toString();
};

export const useFetchAssetRecommendation = () => {
  const fetchAssetRecommendation = useCallback(({ website, prompt }) => {
    if (!website) {
      return Promise.reject(new Error('website is required'));
    }

    const isUseMock = website.includes('mock_url_no_image.com') || website.includes('mock_url_with_image.com');
    const url = isUseMock ? 'api/asset?useMock=true' : 'api/asset';

    const data = { Website: getUrlWithProtocol(website) };
    if (prompt) {
      data.Prompt = prompt;
    }
    const options = {
      ...DefaultOptions,
      url,
      data,
    };
    return odata.post(options);
  }, []);

  const lookupAssetRecommendation = useCallback((lookupId, email, signature) => {
    const isUseMock = lookupId === 'mockCacheId' || lookupId === 'noImageMockCacheId';
    let url = isUseMock ? `api/asset?lookupId=${lookupId}&useMock=true` : `api/asset?lookupId=${lookupId}`;
    if (email && signature) {
      url = `${url}&email=${email}&signature=${signature}`;
    }
    const options = {
      ...DefaultOptions,
      url,
    };
    return odata.get(options);
  }, []);

  return {
    fetchAssetRecommendation,
    lookupAssetRecommendation,
  };
};

export const useSendOnboardEmail = () => {
  const sendOnboardEmail = useCallback((signUpUrl, userInfo = {}, website) => {
    if (!userInfo.email) {
      return Promise.reject(new Error('email is required'));
    }

    const { cacheId: lookupId } = extractCacheRelatedId(signUpUrl);
    const resumeUrl = getResumeUrl(signUpUrl, userInfo.email);
    const isUseMock = typeof website === 'string' && (website.includes('mock_url_no_image.com') || website.includes('mock_url_with_image.com'));
    const url = `api/asset/onboard?lookupId=${lookupId}&resumeUrl=${encodeURIComponent(resumeUrl)}&emailVersion=2${isUseMock ? '&useMock=true' : ''}`;
    const localeData = {
      Country: 'US',
      Locale: 'en-US',
    };
    const mkt = fallbackMkt(extractMkt());
    if (mkt && mkt.split('-').length > 1) {
      const [, country] = mkt.split('-');
      localeData.Country = country;
      localeData.Locale = mkt;
    }

    const options = {
      ...DefaultOptions,
      url,
      data: {
        Email: userInfo.email,
        Phone: userInfo.phone,
        Name: `${userInfo.firstName} ${userInfo.lastName}`,
        ...localeData,
      },
    };
    return odata.post(options);
  }, []);

  return {
    sendOnboardEmail,
  };
};

